import { Button, Modal } from '@/components/ui'
import type { ShipConfirmationSplit, ShipConfirmationTracker } from '../api/types'

export interface UnsplitConfirmModalProps {
  tracker: ShipConfirmationTracker | null
  splits: ShipConfirmationSplit[]
  isPending: boolean
  onCancel: () => void
  onConfirm: () => void
}

const RESOLVED_STAGES = new Set(['confirmed', 'received', 'cancelled'])

/**
 * "Unsplit" — destructive-confirm Modal (Drawer > inline > modal per
 * ops/CLAUDE.md, but a bounded yes/no confirmation is exactly the
 * modals-are-last-resort exception). Copy branches on whether any split
 * already carries confirmation evidence, since that's what's actually
 * at risk of being discarded.
 */
export function UnsplitConfirmModal({
  tracker,
  splits,
  isPending,
  onCancel,
  onConfirm,
}: UnsplitConfirmModalProps) {
  if (!tracker) return null

  const hasEvidence = splits.some((s) => RESOLVED_STAGES.has(s.stage))

  return (
    <Modal
      open={tracker !== null}
      onOpenChange={(next) => {
        if (!next) onCancel()
      }}
      tone="danger"
      title="Unsplit this line?"
      description={
        hasEvidence
          ? `This will discard the ${splits.length} split shipments for ${tracker.odoo_po_name}, including confirmation evidence already recorded — the line reverts to a single, unconfirmed line. This cannot be undone.`
          : `This will discard the ${splits.length} planned splits for ${tracker.odoo_po_name} and restore it to a single line item.`
      }
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" loading={isPending} onClick={onConfirm}>
            Unsplit
          </Button>
        </>
      }
    />
  )
}
